import { profileResource } from '@/resources/profile.resources';
import { ActionContext } from 'vuex';
import { IProfile } from '@/modules/Profile/interfaces/profile.interface';
import router from "@/router";
import _ from 'lodash';
import {AppError} from "@/plugins/axios";
import Vue from 'vue';
import {IBook} from "@/interfaces/book.interface";
import {httpResource} from "@/resources/http.resource";
import {IRequestProfileSet} from "@/modules/Profile/interfaces/request-profile-set.interface";
import {IAppState} from "@/interfaces/app-state.interface";
import {bus} from "@/plugins/bus";
import {IProfileRequest} from "@/modules/Profile/interfaces/profile-request.interface";
import {IApp} from "@/interfaces/app.interface";
import {IRequestAuth} from "@/modules/Auth/interfaces/request-auth.interface";

const CONFIRM_EMAIL = 'confirm_email';

type ProfileStoreContext = ActionContext<IProfile.IState, IAppState>;

export const ProfileStore: ProfileStoreContext = {
  // @ts-ignore
  namespaced: true,

  state: {
    profileData: {
      surName: '',
      firstName: '',
      middleName: '',
      email: '',
      phone: '',
      inn: '',
      parkChoice: [],
      carsXParksFlag: false,
      extModeFlag: false,
      confirmEmail: '',
      corpName: '',
      password: '',
    },
    defaultForm: {
      surName: '',
      firstName: '',
      middleName: '',
      email: '',
      phone: '',
      parkChoice: [],
      extModeFlag: false,
      carsXParksFlag: false,
      password: false,
    },
    emailIsConfirm: false,
    refMode: false,
    refCode: '',
  },

  getters: {
    hasProfileChanges(state: IProfile.IState) { return Object.keys(state.defaultForm).some(key => state.defaultForm[key] !== state.profileData[key]); },
  },

  mutations: {
    setProperty(state: any, { name, value }: { name: string, value: any }) { Vue.set(state, name, value); },
    setPersonalData (state: any, personalData: IProfile.DataProfile) { state.profileData = { ...personalData } as IProfile.DataProfile; },
    setConfirmEmail (state: any, confirmEmail: string) { state.profileData.confirmEmail = confirmEmail; },
    confirmEmail(state: any) { state.emailIsConfirm = true; },
    setProfileDataProperty(state: any, { name, value }: { name: string, value: any }) { Vue.set(state.profileData, name, value); },
    updateProfileProp(state: IProfile.IState, data: { name: string; value: any; }) { Vue.set(state.profileData, data.name, data.value); },
    updateDefaultFormProp(state: IProfile.IState, data: { name: string; value: any; }) { Vue.set(state.defaultForm, data.name, data.value); },
  },

  actions: {
    async init ({ rootState, commit, dispatch }: ProfileStoreContext) {
      try {
        const response = await profileResource.get(rootState.auth.token, '');
        const personalData = response.data.result;
        commit('setPersonalData', personalData);
      }
      catch (error) { await dispatch('handleError', error, { root: true }); }
    },

    changePassword({ state, rootState, dispatch, commit }: ActionContext<any, any>, password: any) {
      return profileResource.changePassword(rootState.auth.token, state.profileData.personalEmail, password.old, password.new)
        .then(() => dispatch('dialog/closeDialog', { blockName: 'profile', dialogName: 'changePasswordPopup' }, { root: true }))
        .then(() => dispatch('openSuccessesNotice', 'Пароль успешно изменен.'))
        .catch(error =>  {
          let params: any;
          if (error instanceof AppError && error.status === IProfile.STATUS_ERROR_PASSWORD_NOT_CONFIRMED) {
            params = {
              textId: 'error.passwordNotConfirmed',
            };
          }
          else {
            params = {
              textId: 'error.changePassword',
              responseData: error.response.data,
              requestData: error.response.config.data,
            };
          }
          return dispatch('error/open', params, { root: true });
        });
    },

    changeEmail({ state, rootState, dispatch, commit }: ActionContext<any, any>, newDataEmail: any) {
      return profileResource.changeEmail(rootState.auth.token, newDataEmail.newEmail, newDataEmail.password)
        .then(response => {
          commit('setConfirmEmail', response.data.result.confirmEmail);
        })
        .then(() => dispatch('dialog/closeDialog', { blockName: 'profile', dialogName: 'changeEmailPopup' }, { root: true }))
        .then(() => dispatch('openSuccessesNotice', 'Запрос на изменение E-mail отправлен.'))
        .catch(error => {
          let params: any;
          if (error instanceof AppError) {
            switch (error.status) {
              case IProfile.STATUS_ERROR_EMAIL_NOT_CONFIRMED:
                params = {textId: 'error.emailNotConfirmed'};
                break;

              case IProfile.STATUS_ERROR_USER_IS_EXIST_IN_DB:
                params = {textId: 'error.userExistInDataBase'};
                break;

              default:
                params = {
                  textId: 'error.changeEmail',
                  responseData: error.response.data,
                  requestData: error.response.config.data,
                };
            }
          }
          else {
            params = {
              textId: 'error.changeEmail',
              responseData: error.response.data,
              requestData: error.response.config.data,
            };
          }
          return dispatch('error/open', params, { root: true });
        });
    },

    confirmEmail({ rootState, commit }: ProfileStoreContext) {
      let confirmEmail = rootState.route.query[CONFIRM_EMAIL];
      if (confirmEmail && confirmEmail !== '') {
        return profileResource.confirmEmail(rootState.auth.token, confirmEmail)
          .then(() => {
            const query = _.clone(rootState.route.query);
            delete query[CONFIRM_EMAIL];
            router.replace({query});
            commit('confirmEmail');
          })
          .catch(() => {});
      }
    },

    async saveChanges({ state, commit, dispatch }: ProfileStoreContext) {
      let asyncAction = async () => {
        let params: any = {
          command: IRequestProfileSet.Commands.CONTACTS,
          ...state.profileData,
        };
        if (state.refMode) {
          params.refFlag = state.refMode;
          params.refCode = state.refCode;
        }
        const response = await httpResource.httpAction({  action: IRequestProfileSet.ACTION, params });

        if (state.refMode) {
          await dispatch('auth/_setToken', response.data.result.newToken, { root: true });
          commit('auth/setProperty', { name: 'tokenWasChecked', value: false }, { root: true });
        }

        commit('auth/updateUserAccessProp', {
          name: 'extModeFlag',
          value: state.profileData.extModeFlag
        }, { root: true });
        commit('auth/updateUserAccessProp', {
          name: 'persName',
          value: `${state.profileData.surName} ${state.profileData.firstName}`
        }, { root: true });
        await dispatch('openSuccessesNotice', 'Данные успешно сохранены.');
        await dispatch('book/getBook', {bookNames: [IBook.TableName.parks], needUpdate: true}, {root: true});
      };

      return await dispatch('defaultResourceAction',  { asyncAction }, { root: true });
    },

    async openSuccessesNotice({ dispatch }: ProfileStoreContext, text: string) {
      bus.$notify({
        title: 'Успешно',
        message: text,
        type: 'success'
      });
    },

    async saveExtModeFlag({ state, commit, dispatch }: ProfileStoreContext, payload: { value: boolean; }) {
      commit('auth/updateUserAccessProp', { name: 'extModeFlag', value: payload.value }, { root: true });
      let asyncAction = async () => {
        let action = IProfileRequest.Action.Set;
        let params = { command: IProfileRequest.Command.SetExtModeFlag, extModeFlag: payload.value };
        await httpResource.httpAction({ action, params });
      };
      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },

    async saveOfertaFlag({ state, commit, dispatch }: ProfileStoreContext, payload: { value: boolean; }) {
      commit('auth/updateUserAccessProp', { name: 'extModeFlag', value: payload.value }, { root: true });
      let asyncAction = async () => {
        let action = IProfileRequest.Action.Set;
        let params = { command: IProfileRequest.Command.SetOfertaFlag };
        let request = await httpResource.httpAction({ action, params });
        if (request.data.result && request.data.result.refCode) {
          let routePayload = {
            routeName: IApp.ROUTE_NAME.PAGE_PROFILE,
            query: {
              ref: request.data.result.refCode,
            },
          };
          await dispatch('openPage', routePayload, {root: true});
        }
      };
      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },

    async openSelectCorpPopup({ state, commit, dispatch }: ProfileStoreContext) {
      let asyncAction = async () => {
        let action = IRequestAuth.Action.Auth;
        let params = { command: IRequestAuth.Command.ReLogin };
        let request = await httpResource.httpAction({ action, params });
        if (request.data.result && request.data.result.userCorps) {
          await dispatch('dialog/openDialog', {
            blockName: 'auth',
            dialogName: 'loginSelectCorp',
            payload: {
              reLogin: true,
              userCorps: request.data.result.userCorps,
            },
          }, {root: true});
        }
      };
      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },

    async reLogin({ dispatch }: ProfileStoreContext, { loginId }: { loginId: number; }) {
      let asyncAction = async () => {
        let action = IRequestAuth.Action.Auth;
        let params = {
          command: IRequestAuth.Command.ReLogin,
          loginId,
        };
        let request = await httpResource.httpAction({ action, params });
        await dispatch('auth/_setToken', request.data.result.token, { root: true });
        document.location.reload();
      };
      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
    },
  }
};
